import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";

const visits = [
  { slug: "chateau-versailles", name: "Château de Versailles", desc: "Galerie des Glaces, jardins, Trianon", distance: "~20 km", duration: "4-8h", image: "https://images.pexels.com/photos/13987557/pexels-photo-13987557.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "fontainebleau", name: "Château de Fontainebleau", desc: "800 ans d'histoire, forêt, Napoléon", distance: "~70 km", duration: "4-8h", image: "https://images.unsplash.com/photo-1597048534858-e4e6bd49a3ec?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85" },
  { slug: "giverny-monet", name: "Giverny — Jardins de Monet", desc: "Nymphéas, pont japonais, art impressionniste", distance: "~75 km", duration: "4-8h", image: "https://images.unsplash.com/photo-1656719529536-a7579c2778a7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85" },
  { slug: "reims-champagne", name: "Reims & Champagne", desc: "Cathédrale, caves de Champagne, vignobles", distance: "~145 km", duration: "8-10h", image: "https://images.pexels.com/photos/13598485/pexels-photo-13598485.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "chateaux-loire", name: "Châteaux de la Loire", desc: "Chambord, Chenonceau, Amboise", distance: "~190 km", duration: "10-12h", image: "https://images.unsplash.com/photo-1763828483140-1956a82a530e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85" },
  { slug: "plages-debarquement-normandie", name: "Plages du Débarquement", desc: "Omaha Beach, cimetière américain, Pointe du Hoc", distance: "~280 km", duration: "12-14h", image: "https://images.pexels.com/photos/17892502/pexels-photo-17892502.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { slug: "bruges-belgique", name: "Bruges — Belgique", desc: "Canaux, beffroi, chocolat, bières artisanales", distance: "~310 km", duration: "12-14h", image: "https://images.unsplash.com/photo-1759338045500-0a219299c612?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85" },
];

const VisitesIndexPage = () => {
  const { t } = useTranslation();

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="destinations-index-page">
      <SEO
        title="Destinations avec Chauffeur Privé depuis Paris"
        path="/destinations"
        description="Organisez une journée visite avec chauffeur privé ALTURA : Reims & Champagne, Giverny, Châteaux de la Loire, Normandie, Bruges, Versailles, Fontainebleau. Devis sur mesure."
      />

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">ALTURA · Journées avec chauffeur</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] mb-4">
              Nos destinations
            </h1>
            <p className="text-[#1A1A1A]/70 text-base max-w-2xl mx-auto font-light">
              Découvrez les plus belles destinations depuis Paris avec votre chauffeur privé. Chaque visite est personnalisée selon vos envies.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {visits.map((visit, i) => (
              <motion.div
                key={visit.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Link to={`/destinations/${visit.slug}`} className="block bg-white border border-[#1A1A1A]/10 overflow-hidden group hover:border-[#1A1A1A]/30 transition-all" data-testid={`visit-card-${visit.slug}`}>
                  <div className="h-48 overflow-hidden">
                    <img src={visit.image} alt={visit.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <h2 className="font-heading text-lg text-[#1A1A1A] mb-1">{visit.name}</h2>
                    <p className="text-[#1A1A1A]/60 text-sm mb-3">{visit.desc}</p>
                    <div className="flex items-center gap-4 text-[#1A1A1A]/40 text-xs">
                      <span className="flex items-center gap-1"><MapPin size={12} /> {visit.distance}</span>
                      <span className="flex items-center gap-1"><Clock size={12} /> {visit.duration}</span>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-[#1A1A1A] text-sm font-medium group-hover:gap-3 transition-all">
                      Découvrir <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default VisitesIndexPage;
